import { type CSSProperties, useEffect, useState } from 'react';
import IconFlash from '@/components/icons/IconFlash';
import IconNote from '@/components/icons/IconNote';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { IndexItem } from '@/features/webinar/types';
import { getFontFamily, getFormattedDate } from '@/lib/utils';
import { ArrowLeftIcon, ArrowRightIcon, Clock, Plus, Wifi, WifiHigh, WifiLow } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { useTranslation } from 'react-i18next';
import CurvedCorner from '@/components/ui/CurvedCorner';
import { PoweredByLogo } from '@/components/pages/public/PoweredByLogo';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { UseFormReturn } from 'react-hook-form';
import PreviewQuiz from './PreviewQuiz';
import PreviewSurvey from './PreviewSurvey';
import type { RootState } from '@/app/store';
import { toWebinarLng } from '@/lib/webinarLanguage';
import { listPresenters, listSpeakers } from '@/features/webinar/webinarThunks';

const toolsData = [
  {
    id: 1,
    img: '/assets/tools-1.png',
  },
  {
    id: 2,
    img: '/assets/tools-2.png',
  },
  {
    id: 3,
    img: '/assets/tools-3.png',
  },
];

type QuizFormValues = {
  name: string;
  subtitle: string;
  questions: {
    id?: number;
    textContent: string;
    type: 'SINGLE_CHOICE';
    options: {
      textContent: string;
      correct: boolean;
      position?: number | null;
    }[];
  }[];
};

type SurveyFormValues = {
  title: string;
  subtitle: string;
  questions: {
    id: number;
    question: string;
    answerType: '1' | '2';
    answers: string[];
  }[];
};

const PREVIEW_SAMPLE_DATE = '2025-01-01T09:00:00.000Z';

export default function PreviewWaitingRoomWebinar({
  visualIdentity,
  theme,
  accentColor,
  backgroundColor,
  contrastColor,
  backgroundImage,
  mainImage,
  mainVideo,
  showMainMedia,
  showLogo,
  showSpeakers,
  showGuests,
  showIndex,
  indexItems,
  showPreWebinarActivity,
  hasWaitingRoomQuiz,
  hasWaitingRoomSurvey,
  interactionType,
  hasQuizFormData,
  hasSurveyFormData,
  quizForm,
  surveyForm,
}: {
  visualIdentity: WorkspaceVisualIdentity;
  theme: 'LIGHT' | 'DARK';
  accentColor: string;
  backgroundColor: string;
  contrastColor: string;
  backgroundImage?: string;
  mainImage?: string | null;
  mainVideo?: string | null;
  showMainMedia?: boolean;
  showLogo?: boolean;
  showSpeakers?: boolean;
  showGuests?: boolean;
  showIndex?: boolean;
  indexItems?: IndexItem[];
  showPreWebinarActivity?: boolean;
  hasWaitingRoomQuiz?: boolean;
  hasWaitingRoomSurvey?: boolean;
  interactionType?: 'SURVEY' | 'QUIZ' | 'NONE';
  hasQuizFormData?: boolean;
  hasSurveyFormData?: boolean;
  quizForm?: UseFormReturn<QuizFormValues>;
  surveyForm?: UseFormReturn<SurveyFormValues>;
}) {
  const [isToolsActive, setIsToolsActive] = useState(false);
  const [isSurveyActive, setIsSurveyActive] = useState(false);
  const [isQuizActive, setIsQuizActive] = useState(false);
  const [toolsStep, setToolsStep] = useState(1);

  const { t } = useTranslation('webinars');
  const dispatch = useAppDispatch();
  const currentWaitingRoomPage = useAppSelector((state) => state.webinar.currentWaitingRoomPage);
  const currentWebinar = useAppSelector((state: RootState) => state.webinar.currentWebinar);
  const isEvergreenWebinar = currentWebinar?.type === 'EVERGREEN';
  const { t: tPreview } = useTranslation('webinars', {
    lng: toWebinarLng(currentWebinar?.language),
  });
  const currentWebinarSession = useAppSelector(
    (state: RootState) => state.webinar.currentWebinarSession,
  );
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);
  const presenters = useAppSelector((state: RootState) => state.webinar.presenters);
  const speakers = useAppSelector((state: RootState) => state.webinar.speakers);
  const currentUser = useAppSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (showSpeakers && currentWebinar?.id && workspaceId) {
      dispatch(listPresenters({ workspaceId, webinarId: currentWebinar.id }));
    }
  }, [showSpeakers, currentWebinar?.id, workspaceId, dispatch]);

  useEffect(() => {
    if (showGuests && currentWebinar?.id && workspaceId) {
      dispatch(listSpeakers({ workspaceId, webinarId: currentWebinar.id, page: 0, size: 100 }));
    }
  }, [showGuests, currentWebinar?.id, workspaceId, dispatch]);

  const webinarGuests = speakers.filter((s) => s.webinarId === currentWebinar?.id);

  const containerStyle: CSSProperties = backgroundImage
    ? {
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : {
        background: backgroundColor,
      };

  const firstPresenterName =
    currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : presenters.length > 0
        ? presenters[0].displayName ||
          `${presenters[0].userFirstName ?? ''} ${presenters[0].userLastName ?? ''}`.trim()
        : 'John Doe';

  const renderWifiIcon = (quality: 'high' | 'medium' | 'low' = 'high') => {
    switch (quality) {
      case 'high':
        return <Wifi size={24} color="green" />;
      case 'medium':
        return <WifiHigh size={24} color="green" />;
      case 'low':
        return <WifiLow size={24} color="red" />;
      default:
        return <Wifi size={24} color="green" />;
    }
  };

  const previewDate = currentWebinarSession?.utcPlannedDate
    ? getFormattedDate(currentWebinarSession.utcPlannedDate, 'Europe/Paris')
    : getFormattedDate(PREVIEW_SAMPLE_DATE, 'Europe/Paris');

  return (
    <div
      className="p-4 sm:p-12 lg:p-8 w-full min-h-[600px] rounded-2xl relative flex items-center justify-center"
      style={containerStyle}
    >
      <div className="w-full max-w-[1440px] mx-auto flex flex-col lg:flex-row items-start gap-12 lg:gap-10 justify-between">
        {/* left side */}
        <div className="w-full flex-1 flex flex-col gap-8">
          {visualIdentity?.logoUrl && showLogo && (
            <img
              src={visualIdentity?.logoUrl}
              alt="logo"
              width={140}
              height={64}
              className="object-contain mx-auto lg:mx-0"
            />
          )}

          <h1
            className={`text-[32px] font-semibold text-left ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
            style={{
              fontFamily: getFontFamily(visualIdentity?.fontFamily),
            }}
          >
            {t('waitingRoom.publicPage.greeting')}{' '}
            <span
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                color: accentColor,
              }}
            >
              {firstPresenterName}
              {t('waitingRoom.publicPage.greetingEnd')}
            </span>
          </h1>

          <div className="flex flex-col gap-4 items-start justify-center">
            <h2
              className={`text-2xl font-bold ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              {/* titre */}
              {currentWebinar?.title}
            </h2>

            <p
              className={`text-sm font-normal ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
              }}
            >
              {/* description */}
              {currentWebinar?.description}
            </p>

            {showSpeakers && presenters.length > 0 && (
              <div className="flex flex-col gap-4">
                {presenters.map((presenter) => {
                  const displayName = presenter.overrideFullName || presenter.displayName;
                  const pictureUrl = presenter.userPictureUrl;
                  const job = presenter.jobTitle || '';

                  return (
                    <div key={presenter.id} className="flex items-center gap-4">
                      <Avatar className="size-12">
                        <AvatarImage src={pictureUrl ?? undefined} className="object-cover" />
                        <AvatarFallback>
                          {displayName
                            ?.split(' ')
                            .map((n) => n.charAt(0))
                            .join('')
                            .toUpperCase()
                            .slice(0, 2) ?? '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p
                          className={`text-sm ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                          }}
                        >
                          {tPreview('waitingRoom.publicPage.presenterBy')}
                          <span
                            className="text-xl font-semibold"
                            style={{
                              fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            }}
                          >
                            {displayName}
                          </span>{' '}
                        </p>
                        {job && (
                          <p
                            className={`text-sm ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                            style={{
                              fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            }}
                          >
                            {job}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showGuests && webinarGuests.length > 0 && (
              <div className="flex flex-col gap-4">
                {webinarGuests.map((guest) => {
                  const displayName = guest.guestFullName || guest.displayName;
                  const job = guest.jobTitle || '';

                  return (
                    <div key={guest.id} className="flex items-center gap-4">
                      <Avatar className="size-12">
                        <AvatarImage
                          src={guest.guestPictureUrl ?? undefined}
                          className="object-cover"
                        />
                        <AvatarFallback>
                          {displayName
                            ?.split(' ')
                            .map((n) => n.charAt(0))
                            .join('')
                            .toUpperCase()
                            .slice(0, 2) ?? '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p
                          className={`text-sm ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                          style={{
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                          }}
                        >
                          {tPreview('waitingRoom.publicPage.with')}
                          <span
                            className="text-xl font-semibold"
                            style={{
                              fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            }}
                          >
                            {displayName}
                          </span>{' '}
                        </p>
                        {job && (
                          <p
                            className={`text-sm ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                            style={{
                              fontFamily: getFontFamily(visualIdentity?.fontFamily),
                            }}
                          >
                            {job}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {showIndex && indexItems && indexItems.length > 0 && (
              <div className="w-full">
                <p
                  className={`text-sm pb-4 text-left ${theme === 'DARK' ? 'text-white' : 'text-black'}`}
                  style={{
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  }}
                >
                  {t('waitingRoom.indexTitle')}
                </p>
                <Accordion
                  type="single"
                  collapsible
                  defaultValue={!isEvergreenWebinar && indexItems[0] ? `item-1` : undefined}
                  className="space-y-[10px]"
                >
                  {indexItems.map((item, index) => {
                    const hasDescription = item.description && item.description.trim().length > 0;
                    return (
                      <AccordionItem
                        key={item.id}
                        value={`item-${index + 1}`}
                        theme={theme || 'LIGHT'}
                      >
                        <AccordionTrigger
                          chevronColor={accentColor}
                          hideChevron={!hasDescription}
                          className={`${!hasDescription ? 'pointer-events-none' : ''} items-start`}
                        >
                          <div className="flex items-start text-left gap-2">
                            <span
                              style={{
                                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                                color: accentColor,
                              }}
                            >
                              {index + 1}.
                            </span>{' '}
                            <span
                              style={{
                                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                                color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                              }}
                            >
                              {item.title}
                            </span>
                          </div>
                        </AccordionTrigger>
                        {hasDescription && (
                          <AccordionContent className="bg-transparent">
                            <span
                              className="text-sm"
                              style={{
                                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                                color: visualIdentity?.theme === 'DARK' ? 'white' : 'black',
                              }}
                            >
                              {item.description}
                            </span>
                          </AccordionContent>
                        )}
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            )}
          </div>
        </div>

        {/* right side */}
        <div className="w-full flex-1 flex flex-col gap-6 lg:sticky top-8 items-center">
          {/* logo welya */}
          <div className="w-full flex items-center justify-end">
            <PoweredByLogo
              theme={theme}
              fontFamily={visualIdentity?.fontFamily ?? undefined}
              isNotLivePage
            />
          </div>

          {/* time remaining */}
          <div
            className="w-full flex flex-col items-center gap-2 p-4 rounded-2xl border"
            style={{
              backgroundColor:
                theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              borderColor: contrastColor,
            }}
          >
            <div className="flex items-start md:items-center gap-1">
              <Clock className="size-4 md:size-6 text-[#9E9FA6]" />
              <div
                className="text-[13px] sm:text-sm font-bold mr-2"
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: theme === 'DARK' ? '#929185' : '#6D6E7A',
                }}
              >
                {previewDate.charAt(0).toUpperCase() + previewDate.slice(1)}
              </div>
            </div>

            <div className="flex flex-col items-center gap-2">
              <div
                className="text-[32px] font-semibold"
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: accentColor,
                }}
              >
                00:23:46
              </div>
              <p
                className="text-sm"
                style={{
                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  color: theme === 'DARK' ? '#929185' : '#6D6E7A',
                }}
              >
                {t('waitingRoom.publicPage.countdown.remainingShort')}
              </p>
            </div>

            <p
              className="text-xs text-center flex items-center gap-1 flex-wrap justify-center"
              style={{
                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                color: theme === 'LIGHT' ? '#17805C' : '#C0FFEA',
              }}
            >
              <span className="text-base font-medium">1218</span>{' '}
              {t('waitingRoom.publicPage.countdown.attendees_other')}
            </p>
          </div>

          {/* btn */}
          <div
            className={`w-full gap-6 flex flex-col sm:grid ${
              showPreWebinarActivity && (hasWaitingRoomQuiz || hasWaitingRoomSurvey)
                ? 'sm:grid-cols-3'
                : 'sm:grid-cols-2'
            }`}
          >
            {/* connexion */}
            <div
              className="flex items-center lg:items-start xl:items-center flex-row lg:flex-col xl:flex-row gap-2 p-2 rounded-lg"
              style={{
                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              <div>
                <div className="size-10 rounded-lg bg-black flex items-center justify-center">
                  {renderWifiIcon()}
                </div>
              </div>

              <div className="flex flex-col">
                <p
                  className="text-xs"
                  style={{
                    color: theme === 'DARK' ? 'white' : 'black',
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                  }}
                >
                  {t('waitingRoom.publicPage.connection.label')}
                </p>
                <p
                  className="text-sm"
                  style={{
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                    color: theme === 'DARK' ? 'white' : 'black',
                  }}
                >
                  {t('waitingRoom.publicPage.connection.strong')}
                </p>
              </div>
            </div>

            {/* btn explore tool */}
            <button
              type="button"
              className="flex items-center gap-2 p-2 text-left rounded-lg relative justify-between cursor-pointer"
              style={{
                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                borderRadius: isToolsActive ? '10px 10px 0 0' : '10px',
                justifyContent: isToolsActive ? 'center' : 'flex-start',
              }}
              onClick={() => {
                setIsToolsActive(!isToolsActive);
                if (isSurveyActive) {
                  setIsSurveyActive(false);
                }
                if (isQuizActive) {
                  setIsQuizActive(false);
                }
              }}
            >
              <div className="flex items-center lg:items-start xl:items-center flex-row lg:flex-col xl:flex-row gap-2 w-full">
                <div className="shrink-0 size-10 rounded-lg bg-black flex items-center justify-center">
                  <IconFlash color={contrastColor} size={24} />
                </div>

                <p
                  className="text-sm"
                  style={{
                    fontFamily: getFontFamily(visualIdentity?.fontFamily),
                    color: theme === 'DARK' ? 'white' : 'black',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: t('waitingRoom.survey.exploreTool'),
                  }}
                />
                {isToolsActive ? (
                  <Plus
                    className="size-6 transition-all rotate-45 duration-200 ml-auto"
                    style={{ color: contrastColor }}
                  />
                ) : (
                  <Plus
                    className="size-6 transition-all rotate-0 duration-200 ml-auto"
                    style={{ color: contrastColor }}
                  />
                )}
              </div>
              {isToolsActive && (
                <div
                  className="w-full z-0 h-6 absolute  left-0 -bottom-6 "
                  style={{
                    backgroundColor:
                      theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                  }}
                >
                  {isToolsActive &&
                    ((hasWaitingRoomSurvey && hasSurveyFormData) ||
                      (hasWaitingRoomQuiz && hasQuizFormData)) && (
                      <div className="absolute bottom-0 -right-6 size-6 overflow-hidden">
                        <CurvedCorner
                          theme={theme as 'LIGHT' | 'DARK' | undefined}
                          corner="bottom-left"
                        />
                      </div>
                    )}
                  <div className="absolute bottom-0 -left-6 size-6 overflow-hidden">
                    <CurvedCorner
                      theme={theme as 'LIGHT' | 'DARK' | undefined}
                      corner="bottom-right"
                    />
                  </div>
                </div>
              )}
            </button>

            {/* btn quiz/survey */}
            {showPreWebinarActivity &&
              ((hasWaitingRoomSurvey && hasSurveyFormData) ||
                (hasWaitingRoomQuiz && hasQuizFormData)) && (
                <button
                  type="button"
                  className="flex items-center lg:items-start xl:items-center gap-2 p-2 text-left lg:flex-col xl:flex-row lg:justify-start xl:justify-start rounded-lg relative cursor-pointer"
                  style={{
                    backgroundColor:
                      theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                    borderRadius: isSurveyActive || isQuizActive ? '10px 10px 0 0' : '10px',
                  }}
                  onClick={() => {
                    const isSurvey = interactionType === 'SURVEY';
                    const isQuiz = interactionType === 'QUIZ';

                    if (isSurvey) {
                      setIsSurveyActive(!isSurveyActive);
                      if (isQuizActive) {
                        setIsQuizActive(false);
                      }
                    } else if (isQuiz) {
                      setIsQuizActive(!isQuizActive);
                      if (isSurveyActive) {
                        setIsSurveyActive(false);
                      }
                    }

                    if (isToolsActive) {
                      setIsToolsActive(false);
                    }
                  }}
                >
                  <>
                    <div className="size-10 shrink-0 rounded-lg bg-black flex items-center justify-center">
                      <IconNote color={contrastColor} size={24} />
                    </div>

                    <p
                      className="text-sm"
                      style={{
                        fontFamily: getFontFamily(visualIdentity?.fontFamily),
                        color: theme === 'DARK' ? 'white' : 'black',
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          interactionType === 'SURVEY'
                            ? t('waitingRoom.survey.doSurvey')
                            : t('waitingRoom.publicPage.quiz.cta'),
                      }}
                    />
                    {isSurveyActive || isQuizActive ? (
                      <Plus
                        className="size-6 transition-all rotate-45 duration-200 ml-auto"
                        style={{ color: contrastColor }}
                      />
                    ) : (
                      <Plus
                        className="size-6 transition-all duration-200 ml-auto"
                        style={{ color: contrastColor }}
                      />
                    )}
                  </>

                  {/* div connexion btn to div */}
                  {(isSurveyActive || isQuizActive) && (
                    <div
                      className="w-full z-0 h-6  absolute  left-0 -bottom-6 "
                      style={{
                        backgroundColor:
                          theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <div className="absolute bottom-0 -left-6 size-6  ">
                        <CurvedCorner
                          theme={theme as 'LIGHT' | 'DARK' | undefined}
                          corner="bottom-right"
                        />
                      </div>
                    </div>
                  )}
                </button>
              )}
          </div>

          {(showMainMedia || isToolsActive || isSurveyActive || isQuizActive) && (
            <div
              className={`w-full rounded-[20px] ${isToolsActive || isSurveyActive || isQuizActive ? 'p-4' : ''}`}
              style={{
                borderRadius:
                  isSurveyActive || isQuizActive
                    ? '20px 0 20px 20px'
                    : (hasWaitingRoomSurvey && hasSurveyFormData) ||
                        (hasWaitingRoomQuiz && hasQuizFormData)
                      ? '20px'
                      : isToolsActive === false &&
                          isSurveyActive === false &&
                          isQuizActive === false
                        ? '20px'
                        : '20px 0 20px 20px',

                backgroundColor:
                  theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
              }}
            >
              {isToolsActive === false &&
                isSurveyActive === false &&
                isQuizActive === false &&
                (mainVideo ? (
                  <video
                    src={mainVideo}
                    className="w-full h-full object-cover rounded-[20px]"
                    controls
                    muted
                    playsInline
                  />
                ) : mainImage ? (
                  <img
                    src={mainImage}
                    alt="waiting-room-bg"
                    className="w-full h-full object-cover rounded-[20px]"
                  />
                ) : (
                  // Placeholder neutre : pas d'image de démo, on indique juste l'emplacement du média
                  <div
                    className="w-full min-h-[200px] flex items-center justify-center rounded-[20px] border-2 border-dashed p-6 text-center"
                    style={{
                      borderColor:
                        theme === 'DARK' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(31, 31, 31, 0.15)',
                    }}
                  >
                    <p
                      className="text-sm"
                      style={{
                        fontFamily: getFontFamily(visualIdentity?.fontFamily),
                        color: theme === 'DARK' ? '#929185' : '#6D6E7A',
                      }}
                    >
                      {t('waitingRoom.mediaPlaceholder')}
                    </p>
                  </div>
                ))}
              {isToolsActive && (
                <>
                  {toolsData
                    .filter((tool) => tool.id <= 3 && tool.id === toolsStep)
                    .map((tool) => (
                      <div
                        key={tool.id}
                        className="flex flex-col items-center justify-between h-full gap-4 w-full"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center">
                            {tool.id > 1 && (
                              <ArrowLeftIcon
                                className="size-6 text-[#9E9FA6] cursor-pointer mr-2"
                                onClick={() => {
                                  setToolsStep(tool.id - 1);
                                }}
                              />
                            )}
                            <h3
                              className="text-xl font-medium"
                              style={{
                                color: theme === 'DARK' ? 'white' : 'black',
                                fontFamily: getFontFamily(visualIdentity?.fontFamily),
                              }}
                            >
                              <span
                                style={{
                                  color: theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                                  fontFamily: getFontFamily(visualIdentity?.fontFamily),
                                }}
                              >
                                {tool.id}/3
                              </span>{' '}
                              {t(`waitingRoom.publicPage.tools.title${tool.id}` as const)}
                            </h3>
                          </div>
                          {tool.id < 3 && (
                            <ArrowRightIcon
                              className="size-6 text-[#9E9FA6] cursor-pointer"
                              onClick={() => {
                                setToolsStep(tool.id + 1);
                              }}
                            />
                          )}
                        </div>
                        <p
                          className="text-sm"
                          style={{
                            color: theme === 'DARK' ? 'white' : '#9E9FA6',
                            fontFamily: getFontFamily(visualIdentity?.fontFamily),
                          }}
                        >
                          {t(`waitingRoom.publicPage.tools.description${tool.id}` as const)}
                        </p>

                        <img
                          src={tool.img}
                          alt={t(`waitingRoom.publicPage.tools.title${tool.id}` as const)}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    ))}
                </>
              )}

              {isSurveyActive && surveyForm && (
                <PreviewSurvey
                  form={surveyForm}
                  visualIdentity={visualIdentity}
                  currentWaitingRoomPage={currentWaitingRoomPage}
                />
              )}

              {isQuizActive && quizForm && (
                <PreviewQuiz
                  form={quizForm}
                  visualIdentity={visualIdentity}
                  currentWaitingRoomPage={currentWaitingRoomPage}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

{
  /* <div
className={`w-full  rounded-[20px] `}
style={{
  borderRadius: '20px',

  backgroundColor:
    theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
}}
>
<img
  src="/assets/waiting-room.png"
  alt="waiting-room-bg"
  className="w-full h-full object-cover rounded-[20px]"
/>
</div> */
}
